import { Sheet } from '@sunwebgroup/ui-components/uds'

import * as React from 'react'

import { styled } from 'styled-components'

import { useCMSPageField } from '@/cms/fields'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { FavoriteToggleButton } from '@/scopes/favourites/components/FavoriteToggleButton/FavoriteToggleButton'
import { useFavouriteProduct } from '@/scopes/favourites/domain/hooks'
import { SearchLocation, SearchResult } from '@/scopes/search/search/infrastructure/api/types'
import { LogInSheet } from './LogInSheet'

interface FavoriteButtonProps {
  result: SearchResult
  className?: string
}

function getLocationProps(locations: SearchLocation[]) {
  const country = locations.find(loc => loc.type === 'country')
  const region = locations.find(loc => loc.type === 'region')
  const city = locations.find(loc => loc.type === 'city')

  return {
    countryId: country?.deskId || '',
    regionId: region?.deskId || '',
    cityId: city?.deskId || '',
    countryName: country?.name || '',
    regionName: region?.name || '',
    cityName: city?.name || '',
    countryEnglishName: country?.englishName || '',
    regionEnglishName: region?.englishName || '',
    cityEnglishName: city?.englishName || '',
  }
}

function FavoriteButton({
  result,
  className,
}: FavoriteButtonProps): React.ReactElement {
  const intl = useIntl()
  const [open, setOpen] = React.useState(false)
  const { isLoggedIn } = useUser()
  const contextItemId = useCMSPageField('contextItemId')
  const { isFavourite, toggleFavouriteState } = useFavouriteProduct(result.id, contextItemId)

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (!isLoggedIn) {
        setOpen(true)
        return
      }
      const { occupancyInfo, id, name, locations, collection, rating, userRating, price, campaign, featuredFilters, departureDateDetail } = result
      const { cityId, countryId, regionId, countryName, regionName, cityName, countryEnglishName, regionEnglishName, cityEnglishName } = getLocationProps(locations)
      toggleFavouriteState({
        occupancyInfo,
        id,
        name,
        cityId,
        countryId,
        regionId,
        countryName,
        regionName,
        cityName,
        countryEnglishName,
        regionEnglishName,
        cityEnglishName,
        collection,
        rating,
        userRating,
        price,
        campaign,
        featuredFilters,
        departureDateDetail,
        origin: 'search result',
      })
    },
    [toggleFavouriteState, isLoggedIn],
  )

  return (
    <Sheet.Root open={open} onOpenChange={(open: boolean) => !open && setOpen(false)}>
      <Sheet.Trigger>
        <FavoriteToggleButton
          isFavourite={isFavourite}
          onClick={handleClick}
          className={className}
          aria-label={intl.formatMessage({ id: 'add_favorite_label' })}
          data-qa="favorite-button"
          data-qa-state={isFavourite ? 'on' : 'off'}
        />
      </Sheet.Trigger>
      <LogInSheet productId={result.id} onClose={() => setOpen(false)} />
    </Sheet.Root>
  )
}

export default styled(FavoriteButton)``
